/**
 * Connection Handlers
 * Extract connection removal logic for each provider type
 */

import { deleteGoogleTokensFromSupabase, deleteMsftTokensFromSupabase } from '../supabase';

// ============================================================================
// TYPES
// ============================================================================

export interface ConnectionRemovalResult {
  success: boolean;
  message: string;
  connectionType: string;
  details: Record<string, any>;
}

// ============================================================================
// GOOGLE CONNECTION REMOVAL
// ============================================================================

/**
 * Remove all Google connections
 */
export async function removeGoogleConnection(userEmail: string): Promise<ConnectionRemovalResult> {
  try {
    const googleResult = await deleteGoogleTokensFromSupabase(userEmail);
    const success = googleResult !== null;

    return {
      success,
      message: success
        ? 'Google connections removed successfully'
        : 'No Google connections found to remove',
      connectionType: 'google',
      details: { googleDeleted: success },
    };
  } catch (error) {
    throw new Error(`Failed to remove Google connection: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Remove only primary Google connection
 */
export async function removeGooglePrimaryConnection(
  userEmail: string
): Promise<ConnectionRemovalResult> {
  try {
    const googleTokens = await deleteGoogleTokensFromSupabase(userEmail);

    if (googleTokens) {
      // For now, we'll remove all Google tokens since the current schema
      // doesn't distinguish between primary and secondary in separate records
      // This could be enhanced in the future to support partial removal
      return {
        success: true,
        message: 'Google primary connection removed successfully',
        connectionType: 'google-primary',
        details: { googlePrimaryDeleted: true },
      };
    } else {
      return {
        success: false,
        message: 'No Google primary connection found to remove',
        connectionType: 'google-primary',
        details: { googlePrimaryDeleted: false },
      };
    }
  } catch (error) {
    throw new Error(
      `Failed to remove Google primary connection: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Remove only secondary Google connection
 */
export async function removeGoogleSecondaryConnection(
  userEmail: string
): Promise<ConnectionRemovalResult> {
  try {
    const googleTokens = await deleteGoogleTokensFromSupabase(userEmail);

    if (googleTokens) {
      // Similar to primary, we'll remove all Google tokens for now
      // This could be enhanced to support partial removal
      return {
        success: true,
        message: 'Google secondary connection removed successfully',
        connectionType: 'google-secondary',
        details: { googleSecondaryDeleted: true },
      };
    } else {
      return {
        success: false,
        message: 'No Google secondary connection found to remove',
        connectionType: 'google-secondary',
        details: { googleSecondaryDeleted: false },
      };
    }
  } catch (error) {
    throw new Error(
      `Failed to remove Google secondary connection: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

// ============================================================================
// MICROSOFT CONNECTION REMOVAL
// ============================================================================

/**
 * Remove Microsoft connection
 */
export async function removeMicrosoftConnection(userEmail: string): Promise<ConnectionRemovalResult> {
  try {
    const msftResult = await deleteMsftTokensFromSupabase(userEmail);
    const success = msftResult !== null;

    return {
      success,
      message: success
        ? 'Microsoft connection removed successfully'
        : 'No Microsoft connection found to remove',
      connectionType: 'microsoft',
      details: { microsoftDeleted: success },
    };
  } catch (error) {
    throw new Error(
      `Failed to remove Microsoft connection: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

// ============================================================================
// UNIFIED CONNECTION REMOVAL
// ============================================================================

/**
 * Remove connection by type
 */
export async function removeConnectionByType(
  userEmail: string,
  connectionType: 'google' | 'microsoft' | 'google-primary' | 'google-secondary'
): Promise<ConnectionRemovalResult> {
  switch (connectionType) {
    case 'google':
      return removeGoogleConnection(userEmail);

    case 'microsoft':
      return removeMicrosoftConnection(userEmail);

    case 'google-primary':
      return removeGooglePrimaryConnection(userEmail);

    case 'google-secondary':
      return removeGoogleSecondaryConnection(userEmail);

    default:
      throw new Error(
        `Invalid connectionType. Must be: google, microsoft, google-primary, or google-secondary`
      );
  }
}

// ============================================================================
// BATCH CONNECTION REMOVAL
// ============================================================================

/**
 * Remove all connections for a user
 */
export async function removeAllConnections(userEmail: string): Promise<{
  google: boolean;
  microsoft: boolean;
}> {
  let googleDeleted = false;
  let microsoftDeleted = false;

  try {
    const googleResult = await deleteGoogleTokensFromSupabase(userEmail);
    googleDeleted = googleResult !== null;
  } catch (error) {
    console.error('Error deleting Google tokens:', error);
  }

  try {
    const msftResult = await deleteMsftTokensFromSupabase(userEmail);
    microsoftDeleted = msftResult !== null;
  } catch (error) {
    console.error('Error deleting Microsoft tokens:', error);
  }

  return {
    google: googleDeleted,
    microsoft: microsoftDeleted,
  };
}
